/**
 * Single-area decision lens interpretation.
 *
 * Purpose-built for the individual verdict card — NOT shared with compare.
 * Compare is a head-to-head with a winner/loser axis; a single area is just
 * one place, so its "Best for" and "Tradeoff" lines need a different logic
 * core, or else the copy ends up abstract and the same across modes.
 *
 * This module takes a Verdict + active LensMode and returns two short lines
 * that feel meaningfully different across modes for the same suburb.
 *
 * Design rules:
 *   - No new schema. Uses existing tags, reasons, priceRange, verdict.
 *   - No backend. No APIs. No feeds.
 *   - Each mode reasons about what that persona actually cares about:
 *       family    → school access, quietness, safety, family routine
 *       bachelor  → nightlife, commute, rental flexibility, social activity
 *       investor  → entry price, rental demand, resale depth, growth
 *   - Cascades are ordered so the strongest signal for that persona wins;
 *     generic fallbacks only fire when nothing more specific hits.
 */

import type { Verdict } from "../data/verdicts";
import type { LensMode } from "./decision-lens";
import { hasRailAccess } from "./constraint-layer";

export type SingleAreaInterpretation = {
  bestFor: string;
  tradeoff: string;
};

/* -------------------------------------------------------------------------- */
/*  Small helpers                                                             */
/* -------------------------------------------------------------------------- */

function tagSet(v: Verdict): Set<string> {
  return new Set((v.tags || []).map((t) => t.toLowerCase()));
}

function reasonBlob(v: Verdict): string {
  return (v.reasons || []).join(" ").toLowerCase();
}

/**
 * Rough midpoint of a price range, in "k per m²" for AUD MEL or "k per sqft"
 * for INR/HYD, whichever unit is present in the string. We only use the
 * *number*, so the comparisons here are unit-agnostic bands — "is this a
 * premium pocket or a budget pocket" — not exact currency math.
 */
function priceMidK(range: string): number | null {
  const nums = [...range.matchAll(/(\d+(?:\.\d+)?)/g)].map((m) => parseFloat(m[1]));
  if (nums.length === 0) return null;
  return (nums[0] + nums[nums.length - 1]) / 2;
}

/* -------------------------------------------------------------------------- */
/*  Family mode — school access, quietness, safety, family routine            */
/* -------------------------------------------------------------------------- */

function interpretFamily(v: Verdict): SingleAreaInterpretation {
  const t = tagSet(v);
  const reasons = reasonBlob(v);
  const mid = priceMidK(v.priceRange);

  // ---- Best for -----------------------------------------------------------
  let bestFor: string;

  if (v.verdict === "red") {
    bestFor = "families who already know the street, not newcomers";
  } else if (
    t.has("school-catchment") ||
    t.has("family") ||
    t.has("leafy") ||
    t.has("village")
  ) {
    bestFor = "families wanting quieter streets + school access";
  } else if ((t.has("beach") || t.has("waterfront")) && !t.has("nightlife")) {
    bestFor = "families with weekend-beach routines + calmer pockets";
  } else if (t.has("it-corridor")) {
    bestFor = "tech families who want work + school in one suburb";
  } else if (t.has("growth-corridor") || t.has("new-build") || t.has("estate")) {
    bestFor = "families building new-home life on a buyer budget";
  } else if (t.has("cbd") || t.has("nightlife") || t.has("bars")) {
    bestFor = "families choosing inner-city convenience over quiet";
  } else if (t.has("tram-corridor") || t.has("creative") || t.has("cafe-culture")) {
    bestFor = "families okay with a lively edge near weekends";
  } else if (t.has("mature-infra") || t.has("heritage")) {
    bestFor = "families wanting settled streets + known routine";
  } else {
    bestFor = "families wanting weekend life without chaos";
  }

  // ---- Tradeoff -----------------------------------------------------------
  let tradeoff: string;

  if (v.verdict === "red" || /mixed safety|patchy|safety pocket/i.test(reasons)) {
    tradeoff = "patchy safety pockets mean stricter school-run rules";
  } else if (t.has("nightlife") || t.has("cbd") || t.has("bars")) {
    tradeoff = "less quiet after dark; school nights can feel loud";
  } else if (
    // Car-first framing only when there is genuinely no rail access. If rail
    // is present, long-commute is re-framed ("rail's long") further below so
    // we don't contradict a rail-bearing suburb with "no-train" copy.
    !hasRailAccess(v) &&
    (t.has("long-commute") ||
      t.has("no-train") ||
      t.has("car-dependent") ||
      t.has("freeway-dependent"))
  ) {
    tradeoff = "car-first routine; school-run + work drives add up";
  } else if (hasRailAccess(v) && t.has("long-commute")) {
    tradeoff = "rail runs, but peak school-run + CBD commute still 40+ min";
  } else if (t.has("arterial-noise") || t.has("industrial-adjacent")) {
    tradeoff = "main-road or factory noise on some streets";
  } else if (t.has("flood-risk") || t.has("bushfire-risk")) {
    tradeoff = "seasonal flood or bushfire risk to plan around";
  } else if (
    (t.has("growth-corridor") || t.has("new-build") || t.has("estate")) &&
    (t.has("infra-lag") || t.has("low-amenity"))
  ) {
    tradeoff = "new-build lifestyle; amenities still catching up";
  } else if (t.has("premium-inner") || (mid !== null && mid >= 22)) {
    tradeoff = "higher entry price; family budgets stretch tighter here";
  } else if (t.has("tram-corridor") || t.has("creative") || t.has("cafe-culture")) {
    tradeoff = "livelier main strips mean less quiet evenings";
  } else {
    tradeoff = "less excitement after dark, by design";
  }

  return { bestFor, tradeoff };
}

/* -------------------------------------------------------------------------- */
/*  Bachelor mode — nightlife, commute, rental flex, social activity          */
/* -------------------------------------------------------------------------- */

function interpretBachelor(v: Verdict): SingleAreaInterpretation {
  const t = tagSet(v);
  const reasons = reasonBlob(v);
  const mid = priceMidK(v.priceRange);

  // ---- Best for -----------------------------------------------------------
  let bestFor: string;

  if (t.has("nightlife") || t.has("cbd") || t.has("bars")) {
    bestFor = "renters wanting nightlife + walk-home food";
  } else if (t.has("cafe-culture") || t.has("food-scene")) {
    bestFor = "renters living by cafes + weekend brunch culture";
  } else if (t.has("it-corridor")) {
    bestFor = "young pros working the IT corridor + short commute";
  } else if ((t.has("beach") || t.has("waterfront")) && !t.has("family")) {
    bestFor = "renters chasing weekend water + summer scene";
  } else if (
    t.has("tram-corridor") ||
    t.has("metro-tunnel") ||
    t.has("transit") ||
    t.has("activity-centre")
  ) {
    bestFor = "renters wanting nightlife + tram/train access";
  } else if (t.has("heritage") || t.has("cultural") || t.has("creative")) {
    bestFor = "creatives who want street character + cheap eats";
  } else if (t.has("school-catchment") || t.has("family") || t.has("leafy")) {
    bestFor = "solo renters who want a calm pocket near work";
  } else if (t.has("growth-corridor") || t.has("new-build") || t.has("estate")) {
    bestFor = "renters okay with an early-career commute for cheaper rent";
  } else {
    bestFor = "renters wanting a balance of rent + scene";
  }

  // ---- Tradeoff -----------------------------------------------------------
  let tradeoff: string;

  if (v.verdict === "red") {
    tradeoff = "late-night walk home needs a plan";
  } else if (t.has("nightlife") || t.has("cbd") || t.has("bars")) {
    tradeoff = "higher rent and more weekend noise";
  } else if (
    // Guarded against rail-bearing suburbs — a working train station should
    // never read as "car-first" even if the commute is long.
    !hasRailAccess(v) &&
    (t.has("long-commute") ||
      t.has("no-train") ||
      t.has("car-dependent") ||
      t.has("freeway-dependent"))
  ) {
    tradeoff = "car-first lifestyle; weekend plans need logistics";
  } else if (hasRailAccess(v) && t.has("long-commute")) {
    tradeoff = "rail option exists, but social life is a long ride away";
  } else if (t.has("tram-corridor") || t.has("cafe-culture") || t.has("food-scene")) {
    tradeoff = "tram-line rent premium; weekend crowds are constant";
  } else if (t.has("premium-inner") || (mid !== null && mid >= 22)) {
    tradeoff = "cafe-strip rent markup; budget bites fast";
  } else if (/mixed safety|patchy|walk home/i.test(reasons)) {
    tradeoff = "walk home needs thought after midnight";
  } else if (
    t.has("school-catchment") ||
    t.has("family") ||
    t.has("leafy") ||
    t.has("village")
  ) {
    tradeoff = "low-buzz pocket; nightlife is a drive or tram away";
  } else if (t.has("arterial-noise") || t.has("industrial-adjacent")) {
    tradeoff = "road noise cuts into weekend rest";
  } else if (t.has("heritage") || t.has("mature-infra")) {
    tradeoff = "quieter after 10pm; social life is elsewhere";
  } else {
    tradeoff = "quieter weekends than an inner-city pocket";
  }

  return { bestFor, tradeoff };
}

/* -------------------------------------------------------------------------- */
/*  Investor mode — entry price, rental demand, resale depth, growth          */
/* -------------------------------------------------------------------------- */

function interpretInvestor(v: Verdict): SingleAreaInterpretation {
  const t = tagSet(v);
  const reasons = reasonBlob(v);
  const mid = priceMidK(v.priceRange);

  // ---- Researched-evidence path -------------------------------------------
  //
  // When the suburb carries `investorEvidence` (sourced from hotspot
  // reports, infra-pipeline tender notices, employment-corridor growth,
  // supply constraints, migration data), prefer that signal over the
  // tag-only inference below. The tier + one driver + one risk line up
  // with the user's actual question: "is long-term upside here
  // stronger, moderate, or weaker — and *why*?"
  //
  // We never claim certainty. The tier ranks *relative* upside; the
  // driver/risk strings are evidence, not prediction. Timeframe is
  // surfaced inline so the user knows whether we're saying "short
  // (1–2y)", "medium (3–5y)", or "long (7–10y)".
  const ev = v.investorEvidence;
  if (ev) {
    const tf =
      ev.timeframe === "short" ? "1–2y" :
      ev.timeframe === "medium" ? "3–5y" : "7–10y";

    const tierLead =
      ev.tier === "stronger"
        ? `buyers with a ${tf} horizon — evidenced long-term upside`
        : ev.tier === "moderate"
        ? `buyers willing to sit ${tf} for mixed-but-positive upside`
        : `buyers chasing yield today rather than future capital growth`;

    const firstDriver = ev.drivers[0];
    const firstRisk = ev.risks[0] ?? "evidence thin outside the headline drivers";

    // "because X" anchors the tier on concrete evidence. Lower-case the
    // first letter of the driver so it reads as a sentence fragment.
    const driverFragment = firstDriver
      ? ` — ${firstDriver.charAt(0).toLowerCase() + firstDriver.slice(1)}`
      : "";

    return {
      bestFor: `${tierLead}${driverFragment}`,
      tradeoff: firstRisk,
    };
  }

  // ---- Tag-only fallback (legacy path; unchanged logic) -------------------
  let bestFor: string;

  if (t.has("growth-corridor") || t.has("new-build") || t.has("estate")) {
    bestFor = "buyers chasing capital growth on land-release belts";
  } else if (t.has("it-corridor")) {
    bestFor = "buyers riding tech-corridor rental demand";
  } else if (t.has("nightlife") || t.has("cafe-culture") || t.has("food-scene")) {
    bestFor = "buyers leaning on young-professional rental demand";
  } else if (t.has("beach") || t.has("waterfront")) {
    bestFor = "buyers riding weekend + short-stay rental demand";
  } else if (t.has("school-catchment") || t.has("family") || t.has("leafy")) {
    bestFor = "buyers wanting low-churn family tenants";
  } else if (t.has("cbd")) {
    bestFor = "buyers targeting CBD rental depth";
  } else if (t.has("transit") || t.has("activity-centre") || t.has("tram-corridor")) {
    bestFor = "buyers chasing rental demand + stronger tenant appeal";
  } else if (t.has("premium-inner") || t.has("heritage")) {
    bestFor = "buyers chasing resale upside over raw yield";
  } else {
    bestFor = "buyers wanting a steady tenant reputation";
  }

  let tradeoff: string;

  if (v.verdict === "red") {
    tradeoff = "higher vacancy + slower rent growth than headline stats";
  } else if (t.has("cbd") && /oversupply|apartment glut|tower glut/i.test(reasons)) {
    tradeoff = "apartment oversupply holds resale values flat";
  } else if (
    t.has("tram-corridor") &&
    /construction|tower|crane|building activity/i.test(reasons)
  ) {
    tradeoff = "apartment construction pipeline caps short-term yield";
  } else if (t.has("premium-inner") || (mid !== null && mid >= 22)) {
    tradeoff = "higher entry price can compress yield";
  } else if (
    t.has("growth-corridor") ||
    t.has("new-build") ||
    t.has("estate") ||
    t.has("infra-lag")
  ) {
    tradeoff = "cheap entry, but resale depth takes years to build";
  } else if (!hasRailAccess(v) && (t.has("no-train") || t.has("long-commute"))) {
    tradeoff = "tenant pool thinner; rent growth slower";
  } else if (hasRailAccess(v) && t.has("long-commute")) {
    tradeoff = "rail keeps tenants, but rent growth tracks the long commute";
  } else if (/mixed safety|patchy/i.test(reasons)) {
    tradeoff = "vacancy risk if the pocket drifts";
  } else if (t.has("arterial-noise") || t.has("industrial-adjacent")) {
    tradeoff = "road noise narrows tenant pool";
  } else if (t.has("school-catchment") || t.has("family")) {
    tradeoff = "stable yield, but growth caps at family-home bands";
  } else {
    tradeoff = "higher entry price can compress yield";
  }

  return { bestFor, tradeoff };
}

/* -------------------------------------------------------------------------- */
/*  Public entry                                                              */
/* -------------------------------------------------------------------------- */

export function interpretForMode(
  v: Verdict,
  mode: LensMode
): SingleAreaInterpretation {
  switch (mode) {
    case "family":
      return interpretFamily(v);
    case "bachelor":
      return interpretBachelor(v);
    case "investor":
      return interpretInvestor(v);
    case "none":
      // Exploration mode — no persona framing. Callers in Exploration
      // are expected to suppress the lens-specific UI block, so the
      // returned strings should rarely surface. The family fallback
      // here is a defensive default, not a user-visible outcome.
      return interpretFamily(v);
  }
}
